import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { createClient, SupabaseClient } from '@supabase/supabase-js'

export interface StoreVideo {
  id: number
  title: string
  description: string
  video_url: string
  storage_path: string
  is_active: boolean
  created_at: string
  updated_at?: string
}

/**
 * Hook para gestionar videos subiendo DIRECTAMENTE a Supabase Storage desde el navegador
 * Esto evita el límite de 4.5MB de Vercel Serverless Functions
 *
 * IMPORTANTE: Este hook requiere que el usuario esté autenticado con Supabase
 */
export const useSupabaseVideoUpload = () => {
  const [videos, setVideos] = useState<StoreVideo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authenticatedClient, setAuthenticatedClient] = useState<SupabaseClient | null>(null)

  // Inicializar el cliente autenticado
  useEffect(() => {
    const initAuthClient = async () => {
      try {
        // Obtener el token del localStorage (guardado por el login como 'admin_token')
        const adminToken = localStorage.getItem('admin_token')

        if (!adminToken) {
          setAuthenticatedClient(supabase)
          return
        }

        // Crear un cliente de Supabase con el token de autenticación
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://lpphlpmylnedoqukdfib.supabase.co'
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        if (!supabaseAnonKey) {
          setAuthenticatedClient(supabase)
          return
        }

        const client = createClient(supabaseUrl, supabaseAnonKey, {
          global: {
            headers: {
              Authorization: `Bearer ${adminToken}`
            }
          }
        })

        setAuthenticatedClient(client)
      } catch (err) {
        setAuthenticatedClient(supabase)
      }
    }

    initAuthClient()
  }, [])

  // Cargar todos los videos desde la base de datos
  const loadVideos = async () => {
    if (!authenticatedClient) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const { data, error: fetchError } = await authenticatedClient
        .from('store_videos')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) {
        throw fetchError
      }

      setVideos(data || [])
    } catch (err: any) {
      setError(err.message || 'Error al cargar los videos')
    } finally {
      setIsLoading(false)
    }
  }

  // Cargar videos cuando el cliente autenticado esté listo
  useEffect(() => {
    if (authenticatedClient) {
      loadVideos()
    }
  }, [authenticatedClient])

  // Subir video directamente a Supabase Storage
  const uploadVideo = async (file: File, title: string, description: string) => {
    if (!authenticatedClient) {
      return { success: false, error: 'Cliente de Supabase no autenticado' }
    }

    try {
      // 1. Generar nombre único para el archivo
      const timestamp = Date.now()
      const fileName = `video-${timestamp}.${file.name.split('.').pop()}`
      const storagePath = `videos/${fileName}`

      // 2. Subir el archivo directamente a Supabase Storage con el cliente autenticado
      const { error: uploadError } = await authenticatedClient.storage
        .from('store-videos')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        throw new Error(`Error al subir archivo: ${uploadError.message}`)
      }

      // 3. Obtener URL pública del video
      const { data: urlData } = authenticatedClient.storage
        .from('store-videos')
        .getPublicUrl(storagePath)

      const publicUrl = urlData.publicUrl

      // 4. Desactivar todos los videos anteriores
      const { error: deactivateError } = await authenticatedClient
        .from('store_videos')
        .update({ is_active: false })
        .eq('is_active', true)

      if (deactivateError) {
        // Error al desactivar videos anteriores (no es crítico)
      }

      // 5. Insertar registro en la base de datos
      const { data: videoData, error: dbError } = await authenticatedClient
        .from('store_videos')
        .insert([
          {
            title,
            description,
            video_url: publicUrl,
            storage_path: storagePath,
            is_active: true
          }
        ])
        .select()
        .single()

      if (dbError) {
        // Intentar eliminar el archivo de Storage si falla el registro en BD
        await authenticatedClient.storage
          .from('store-videos')
          .remove([storagePath])

        throw new Error(`Error al guardar video: ${dbError.message}`)
      }

      // 6. Recargar la lista de videos
      await loadVideos()

      return { success: true, data: videoData }
    } catch (err: any) {
      return { success: false, error: err.message || 'Error al subir el video' }
    }
  }

  // Eliminar video
  const deleteVideo = async (id: number, storagePath: string) => {
    if (!authenticatedClient) {
      return { success: false, error: 'Cliente de Supabase no autenticado' }
    }

    try {
      // 1. Eliminar archivo de Storage
      const { error: storageError } = await authenticatedClient.storage
        .from('store-videos')
        .remove([storagePath])

      if (storageError) {
        // Error al eliminar de Storage (no es crítico)
      }

      // 2. Eliminar registro de la base de datos
      const { error: dbError } = await authenticatedClient
        .from('store_videos')
        .delete()
        .eq('id', id)

      if (dbError) {
        throw new Error(`Error al eliminar video: ${dbError.message}`)
      }

      // 3. Recargar la lista de videos
      await loadVideos()

      return { success: true }
    } catch (err: any) {
      return { success: false, error: err.message || 'Error al eliminar el video' }
    }
  }

  // Obtener el video activo (para la landing page)
  const getActiveVideo = () => {
    return videos.find(video => video.is_active)
  }

  // Activar un video específico (deactivate all others)
  const activateVideo = async (id: number) => {
    if (!authenticatedClient) {
      return { success: false, error: 'Cliente de Supabase no autenticado' }
    }

    try {
      // 1. Deactivate all videos
      const { error: deactivateError } = await authenticatedClient
        .from('store_videos')
        .update({ is_active: false })
        .neq('id', id)

      if (deactivateError) {
        throw new Error(`Error al desactivar videos anteriores: ${deactivateError.message}`)
      }

      // 2. Activate the selected video
      const { data: videoData, error: activateError } = await authenticatedClient
        .from('store_videos')
        .update({ is_active: true })
        .eq('id', id)
        .select()
        .single()

      if (activateError) {
        throw new Error(`Error al activar video: ${activateError.message}`)
      }

      // 3. Reload the list
      await loadVideos()

      return { success: true, data: videoData }
    } catch (err: any) {
      return { success: false, error: err.message || 'Error al activar el video' }
    }
  }

  return {
    videos,
    activeVideo: getActiveVideo(),
    isLoading,
    error,
    uploadVideo,
    deleteVideo,
    loadVideos,
    activateVideo
  }
}

